import BeeswaxCatchupBehavior from '@/Enums/Beeswax/BeeswaxCatchupBehavior.enum';
import BeeswaxPacing from '@/Enums/Beeswax/BeeswaxPacing.enum';
import BeeswaxPacingBehavior from '@/Enums/Beeswax/BeeswaxPacingBehavior.enum';
import type { BillingModel } from '@/Enums/BillingModel.enum';
import LineItemFormField from '@/Enums/CampaignManagement/LineItemFormField.enum';
import type CreativeType from '@/Enums/Creatives/CreativeType.enum';
import ItemStatus from '@/Enums/ItemStatus.enum';
import type { IProduct } from '@/Interfaces/Product.interface';
import CreativeTypeService from '@/Services/Creative/CreativeType.service';
import BillingModelService from '@/Services/Whitelabels/BillingModel.service';
import { NEW_ITEM_ID } from '@/constants/campaignManagement';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import store from '@/store';
import { URL_RULES } from '@/utils/validationRules';
import * as yup from 'yup';

const nullableNumberValidationSchema = yup
  .number()
  .nullable()
  .transform((value: string) => {
    if (value === null) {
      return null;
    }
    return value || +value === 0 ? +value : null;
  })
  .moreThan(0, 'Must be a positive non-zero number')
  .default(null);

const requiredNumberValidationSchema = (requiredMessage = 'Field is required') =>
  yup
    .number()
    .transform(value => (value ? +value : null))
    .min(0, 'Must be a positive number')
    .required(requiredMessage)
    .default(null);

/**
 * Required on create only, matching the API, which requires a bid in `CreateLineItem.validator`
 * alone: Percent of Media line items saved before that rule still hold a null bid, and their owners
 * have to be able to save unrelated edits. The bid's range and two-decimal cap are enforced by the
 * input mask instead, so an invalid one cannot be typed in the first place.
 */
const bidCpmValidationSchema = nullableNumberValidationSchema.when('id', {
  is: (id: number | string) => id === NEW_ITEM_ID,
  then: schema => schema.required('Bid CPM is required'),
});

/** Orgs that hide the product selection card have no way to pick a product, so it can't be required */
const isProductSelectionHidden = () => !!store.getState()?.currentWhitelabel?.whitelabel?.hideProductSelectionCard;

/** Orgs on Packages target duplicates at a Package, so a Product can't be required */
const hasPackages = () => !!store.getState()?.sharedApi?.packages?.length;

const checkProductRequired = (id: number | string, isRibeyeEntry: boolean | undefined) => {
  if (isProductSelectionHidden()) return false;
  return id === NEW_ITEM_ID || isRibeyeEntry;
};

/**
 * A duplicate of a Product-targeted line item keeps the Product dropdown even when the org also
 * has Packages
 */
const duplicatePackageRequiredTest: yup.TestConfig<number | null | undefined> = {
  name: 'packageRequired',
  message: 'Package is required',
  test(value) {
    const { product } = this.parent as { product?: IProduct | null };
    if (!hasPackages() || product?.id) return true;
    return value != null;
  },
};

const customDomainsAtLeastOneTest = (
  siblingField: LineItemFormField.CustomAllowDomains | LineItemFormField.CustomBlockDomains,
): yup.TestConfig<(string | undefined)[] | undefined> => ({
  name: 'atLeastOneDomain',
  message: 'Please add at least one domain to the Allow or Block list',
  test(value) {
    const parent = this.parent as {
      [LineItemFormField.HasCustomDomainLists]?: boolean;
      [LineItemFormField.CustomAllowDomains]?: string[];
      [LineItemFormField.CustomBlockDomains]?: string[];
    };
    if (!parent[LineItemFormField.HasCustomDomainLists]) return true;
    return (value?.length ?? 0) > 0 || (parent[siblingField]?.length ?? 0) > 0;
  },
});

export const lineItemFormValidationSchema = (billingModel: BillingModel | null) =>
  yup.object().shape({
    id: yup.mixed(),
    isRibeyeEntry: yup.boolean().default(false),
    name: yup.string().required('Line Item Name is required').default(''),
    impressionsBooked: requiredNumberValidationSchema('Impressions Booked is required').test(
      (value, { createError, path }) => {
        const { settings } = store.getState()?.currentWhitelabel?.whitelabel ?? {};

        if (settings?.lineItemImpressionCap && value > settings.lineItemImpressionCap) {
          return createError({
            path,
            message: `Should be less than ${numberToUsFormat(settings.lineItemImpressionCap)}`,
          });
        }

        return true;
      },
    ),
    startDate: yup.object().required(),
    endDate: yup.object().required(),
    soldCpm: nullableNumberValidationSchema,
    // Percent of Media orgs enter their own bid and have no product bid to fall back on, so it is
    // mandatory. Rate card orgs never see the field — their bid comes from the selected product.
    bidCpm: BillingModelService.allowBidCpmControl(billingModel)
      ? bidCpmValidationSchema
      : nullableNumberValidationSchema,
    channel: yup
      .object({ label: yup.string().required(), value: yup.number().required() })
      .required('Channel is required')
      .default(null),
    devices: yup
      .array()
      .default([])
      .test({
        name: 'optionalForRateCard',
        message: 'Please select at least 1 device',
        test: value => !BillingModelService.allowDeviceSelection(billingModel) || (value?.length ?? 0) > 0,
      }),
    product: yup
      .object({ id: yup.number(), name: yup.string() })
      .when(
        [
          'id',
          'isRibeyeEntry',
          LineItemFormField.HasCustomDomainLists,
          LineItemFormField.CustomAllowDomains,
          LineItemFormField.CustomBlockDomains,
        ],
        {
          is: (
            id: number | string,
            isRibeyeEntry: boolean | undefined,
            hasCustomDomainLists: boolean | undefined,
            customAllowDomains: string[] | undefined,
            customBlockDomains: string[] | undefined,
          ) => {
            const isCustomMode =
              hasCustomDomainLists || (customAllowDomains?.length ?? 0) > 0 || (customBlockDomains?.length ?? 0) > 0;
            if (isCustomMode) return false;
            return checkProductRequired(id, isRibeyeEntry);
          },
          then: schema => schema.required('Product is required').default(null),
          otherwise: schema => schema.nullable().default(null),
        },
      ),
    customAllowDomains: yup
      .array()
      .of(yup.string())
      .default([])
      .test(customDomainsAtLeastOneTest(LineItemFormField.CustomBlockDomains)),
    customBlockDomains: yup
      .array()
      .of(yup.string())
      .default([])
      .test(customDomainsAtLeastOneTest(LineItemFormField.CustomAllowDomains)),
    frequencyCaps: yup.object().shape({
      hourly: nullableNumberValidationSchema,
      weekly: nullableNumberValidationSchema,
      daily: nullableNumberValidationSchema,
      monthly: nullableNumberValidationSchema,
    }),
    pacingBehavior: yup
      .mixed()
      .nullable()
      .oneOf([...Object.values(BeeswaxPacingBehavior), null])
      .default(BeeswaxPacingBehavior.Even),
    catchupBehavior: yup
      .mixed()
      .nullable()
      .oneOf([...Object.values(BeeswaxCatchupBehavior), null])
      .default(BeeswaxCatchupBehavior.Even),
    dailyBudget: yup
      .number()
      .nullable()
      .default(null)
      .transform(value => (Number.isInteger(value) ? value : null))
      .moreThan(0, 'Daily Impression Cap must be a positive non-zero value')
      .max(yup.ref('impressionsBooked'), 'Daily Impression Cap must not exceed Impressions Booked'),
    geo: yup.object().shape({
      included: yup.array().default([]),
      excluded: yup.array().default([]),
    }),
    audiences: yup.object().default({}),
    firstPartySegments: yup
      .array()
      .of(
        yup.object().shape({
          value: yup.string().required(),
          label: yup.string().required(),
        }),
      )
      .default([]),
    daypart: yup.object().default({}),
    duplicates: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required('Name is required').default(''),
          startDate: yup.object().required(),
          endDate: yup.object().required(),
          impressionsBooked: requiredNumberValidationSchema('Impression is required'),
          product: yup.object({ id: yup.number(), name: yup.string() }).when([], {
            is: () => isProductSelectionHidden() || hasPackages(),
            then: schema => schema.nullable().default(null),
            otherwise: schema => schema.required('Product is required'),
          }),
          packageId: yup.number().nullable().default(null).test(duplicatePackageRequiredTest),
          channel: yup
            .object({ label: yup.string().required(), value: yup.number().required() })
            .required('Channel is required'),
        }),
      )
      .default([]),
    creatives: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.number().required(),
          associationStatus: yup.string().oneOf(Object.values(ItemStatus)).required(),
          associationWeightRel: yup.number().required(),
          associationWeightPct: yup.number().required(),
          startDate: yup.object().nullable(),
          endDate: yup.object().nullable(),
          clickUrl: yup.string().when('type', {
            is: (val: CreativeType) => CreativeTypeService.hasClickUrl(val),
            then: schema =>
              schema.required(URL_RULES.required.message).test((value, { createError, path }) => {
                const validationResult = URL_RULES.validate(value);
                if (validationResult !== true) {
                  return createError({ path, message: validationResult });
                }

                return true;
              }),
            otherwise: schema => schema.optional().nullable(),
          }),
        }),
      )
      .default([]),
    publisherIds: yup.array().default([]),
    altId: yup.string().default(''),
    pacing: yup.string().default(BeeswaxPacing.Lifetime),
  });
